import { useCallback } from 'react';

import { showNotification } from '@mantine/notifications';

import type { ExportResponse } from '../../generated/generatedOpenAPI';
import { useCreateExport } from '../../store/store';

export interface UseSendToExportSummaryResult {
  /**
   * Creates a new export tab from the given nodes. Resolves to the created export, or
   * `undefined` when there is nothing to send or the request failed.
   */
  sendToExportSummary: (nodeIds: number[]) => Promise<ExportResponse | undefined>;
  /** True while the create is in flight — use it to disable the trigger. */
  isSending: boolean;
}

/**
 * Sends a set of nodes to Export summary as a new export tab (SSTSW-1045).
 *
 * Only node ids go over the wire — the backend resolves barcodes, CDR3s and export numbers
 * from them server-side.Failures are surfaced as a notification
 */
export function useSendToExportSummary(experimentId: string | undefined): UseSendToExportSummaryResult {
  const [createExport, { isLoading }] = useCreateExport();

  const sendToExportSummary = useCallback(
    async (nodeIds: number[]) => {
      if (!experimentId || nodeIds.length === 0) {
        return undefined;
      }
      try {
        return await createExport({
          experimentUuid: experimentId,
          exportCreate: { nodeIds },
        }).unwrap();
      } catch {
        showNotification({
          title: 'Export failed',
          message: 'The selected sequences could not be sent to Export summary. Please try again.',
          color: 'red',
        });
        return undefined;
      }
    },
    [experimentId, createExport],
  );

  return { sendToExportSummary, isSending: isLoading };
}
